import { useEffect, useRef, useState } from "react";
import { apiFetch } from "../api";
import { BASE_URL as u } from "../../commonUrls";
import "./AIChatWidget.css";

const QUICK_PROMPTS = [
  { label: "How am I doing?", emoji: "📊" },
  { label: "Any overdue tasks?", emoji: "⚠️" },
  { label: "Spending this month", emoji: "💸" },
  { label: "Habit streaks", emoji: "🔥" },
];

function renderAiText(text) {
  if (!text) return null;
  const lines = text.split("\n");
  const elements = [];
  let key = 0;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();

    if (!trimmed) {
      elements.push(<div key={key++} className="ai-msg-spacer" />);
      continue;
    }

    const bulletMatch = trimmed.match(/^[-•*]\s+(.+)/);
    const numberedMatch = trimmed.match(/^(\d+)[.)]\s+(.+)/);

    if (bulletMatch) {
      elements.push(
        <div key={key++} className="ai-msg-bullet">
          <span className="ai-msg-bullet-dot">•</span>
          <span>{inlineFmt(bulletMatch[1])}</span>
        </div>
      );
    } else if (numberedMatch) {
      elements.push(
        <div key={key++} className="ai-msg-bullet">
          <span className="ai-msg-bullet-dot">{numberedMatch[1]}.</span>
          <span>{inlineFmt(numberedMatch[2])}</span>
        </div>
      );
    } else if (trimmed.startsWith("**") && trimmed.endsWith("**") && trimmed.length > 4) {
      elements.push(
        <div key={key++} className="ai-msg-heading">{trimmed.slice(2, -2)}</div>
      );
    } else {
      elements.push(<div key={key++} className="ai-msg-line">{inlineFmt(trimmed)}</div>);
    }
  }
  return elements;
}

function inlineFmt(text) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    }
    return part;
  });
}

function TypingDots() {
  return (
    <div className="ai-typing-dots">
      <span /><span /><span />
    </div>
  );
}

export default function AIChatWidget() {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState(null);
  const [action, setAction] = useState(null);
  const [pendingConfirm, setPendingConfirm] = useState(false);
  const bottomRef = useRef(null);
  const textareaRef = useRef(null);
  const greetedRef = useRef(false);
  // Conversation history sent to backend for multi-turn context (max last 12 turns)
  const historyRef = useRef([]);

  useEffect(() => {
    if (!open) return;
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages, loading, open]);

  useEffect(() => {
    if (open && !greetedRef.current && messages.length === 0) {
      greetedRef.current = true;
      sendToAssistant("hi", null, null, false);
    }
    if (open) {
      setTimeout(() => textareaRef.current?.focus(), 120);
    }
  }, [open]);

  const sendToAssistant = async (text, currentAction, currentDraft, isConfirm) => {
    setLoading(true);
    try {
      const body = { input: text };
      if (currentAction) body.action = currentAction;
      if (currentDraft) body.draft = currentDraft;
      if (isConfirm) body.confirm = true;

      // Send conversation history so the AI remembers previous turns
      const history = historyRef.current.slice(-12);
      if (history.length > 0) body.history = history;

      const response = await apiFetch(`${u}/assistant`, {
        method: "POST",
        body: JSON.stringify(body),
      });

      if (!response.ok) throw new Error("AI request failed");

      const data = await response.json();
      const message = data.message || "Done.";
      setMessages((prev) => [...prev, { sender: "ai", text: message }]);

      // Append to history for next turn
      if (text) historyRef.current.push({ role: "user", text });
      historyRef.current.push({ role: "assistant", text: message });

      if (data.needsConfirmation) {
        setDraft(data.draft);
        setAction(data.action);
        setPendingConfirm(true);
      } else {
        setDraft(null);
        setAction(null);
        setPendingConfirm(false);
      }
    } catch {
      setMessages((prev) => [
        ...prev,
        { sender: "ai", text: "Could not reach the assistant. Try again." },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const send = async (overrideText) => {
    const prompt = (overrideText ?? input).trim();
    if (!prompt || loading) return;
    setMessages((prev) => [...prev, { sender: "user", text: prompt }]);
    setInput("");
    setPendingConfirm(false);
    await sendToAssistant(prompt, action, draft, false);
  };

  const confirm = async () => {
    setMessages((prev) => [...prev, { sender: "user", text: "Yes, go ahead." }]);
    setPendingConfirm(false);
    await sendToAssistant("", action, draft, true);
  };

  const cancel = () => {
    setPendingConfirm(false);
    setDraft(null);
    setAction(null);
    setMessages((prev) => [
      ...prev,
      { sender: "ai", text: "Cancelled. What else can I help with?" },
    ]);
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const handleOpen = () => {
    if (!open) {
      greetedRef.current = false;
      // Fresh session: clear messages and history
      setMessages([]);
      historyRef.current = [];
      setDraft(null);
      setAction(null);
      setPendingConfirm(false);
    }
    setOpen((v) => !v);
  };

  const isEmpty = messages.length === 0;

  return (
    <div className="ai-widget-root">
      {open && (
        <div className="ai-widget-panel">
          {/* Header */}
          <div className="ai-widget-head">
            <div className="ai-widget-head-info">
              <div className="ai-widget-avatar-sm">✦</div>
              <div>
                <div className="ai-widget-title">LifeOS Assistant</div>
                <div className="ai-widget-subtitle">Knows your life</div>
              </div>
            </div>
            <button
              type="button"
              className="ai-widget-close"
              onClick={() => setOpen(false)}
              aria-label="Close"
            >
              ✕
            </button>
          </div>

          {/* Body */}
          <div className="ai-widget-body">
            {isEmpty && loading && (
              <div className="ai-widget-greeting-loader">
                <div className="ai-widget-avatar-lg">✦</div>
                <p>Analysing your life...</p>
              </div>
            )}

            {isEmpty && !loading && (
              <div className="ai-widget-empty-state">
                <div className="ai-widget-avatar-lg">✦</div>
                <p className="ai-widget-empty-label">Your personal assistant is ready</p>
                <div className="ai-widget-chips">
                  {QUICK_PROMPTS.map((q) => (
                    <button
                      key={q.label}
                      type="button"
                      className="ai-chip"
                      onClick={() => send(q.label)}
                      disabled={loading}
                    >
                      <span>{q.emoji}</span> {q.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((m, i) => (
              <div
                key={`${m.sender}-${i}`}
                className={m.sender === "user" ? "ai-msg-user-wrap" : "ai-msg-ai-wrap"}
              >
                {m.sender === "ai" && (
                  <div className="ai-msg-avatar">✦</div>
                )}
                <div className={m.sender === "user" ? "ai-msg-user" : "ai-msg-ai"}>
                  {m.sender === "ai" ? renderAiText(m.text) : m.text}
                </div>
              </div>
            ))}

            {loading && messages.length > 0 && (
              <div className="ai-msg-ai-wrap">
                <div className="ai-msg-avatar">✦</div>
                <div className="ai-msg-ai ai-msg-thinking">
                  <TypingDots />
                </div>
              </div>
            )}

            {pendingConfirm && !loading && (
              <div className="ai-confirm-bar">
                <span className="ai-confirm-label">Confirm this action?</span>
                <button type="button" className="ai-confirm-yes" onClick={confirm}>
                  ✓ Yes
                </button>
                <button type="button" className="ai-confirm-no" onClick={cancel}>
                  Cancel
                </button>
              </div>
            )}

            <div ref={bottomRef} />
          </div>

          {/* Input */}
          <div className="ai-widget-input-wrap">
            <textarea
              ref={textareaRef}
              rows={1}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              className="ai-widget-input"
              placeholder="Ask anything about your life..."
              disabled={loading}
            />
            <button
              type="button"
              className="ai-widget-send"
              onClick={() => send()}
              disabled={loading || !input.trim()}
              aria-label="Send"
            >
              ➤
            </button>
          </div>
        </div>
      )}

      <button
        type="button"
        className={`ai-widget-fab${!open && messages.length === 0 ? " ai-widget-fab--pulse" : ""}`}
        onClick={handleOpen}
        aria-label="Open AI assistant"
      >
        <span className="ai-fab-icon">✦</span>
      </button>
    </div>
  );
}
